import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, Package, Settings2 } from "lucide-react";

interface Service {
  id: string;
  name: string;
  price: number;
}

interface ServicePackage {
  id: string;
  name: string;
  description: string;
  discount_type: string;
  discount_value: number;
  is_active: boolean;
  service_ids: string[];
}

const PackagesAdmin = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [packages, setPackages] = useState<ServicePackage[]>([]);
  const [editing, setEditing] = useState<Partial<ServicePackage> | null>(null);
  const [open, setOpen] = useState(false);
  const [bundleType, setBundleType] = useState("percentage");
  const [bundleValue, setBundleValue] = useState(10);
  const [bundleMin, setBundleMin] = useState(2);
  const { toast } = useToast();

  const fetchAll = async () => {
    const [sRes, pRes, piRes, cfgRes] = await Promise.all([
      supabase.from("services").select("id, name, price").order("name"),
      supabase.from("service_packages").select("*").order("created_at"),
      supabase.from("service_package_items").select("package_id, service_id"),
      supabase.from("app_settings").select("key, value").in("key", ["bundle_discount_type", "bundle_discount_value", "bundle_min_services"]),
    ]);

    setServices((sRes.data as Service[]) || []);

    const items = piRes.data || [];
    const pkgs: ServicePackage[] = ((pRes.data || []) as any[]).map((p) => ({
      ...p,
      service_ids: items.filter((i: any) => i.package_id === p.id).map((i: any) => i.service_id),
    }));
    setPackages(pkgs);

    (cfgRes.data || []).forEach((row: any) => {
      const val = typeof row.value === "string" ? row.value.replace(/^"|"$/g, "") : String(row.value);
      if (row.key === "bundle_discount_type") setBundleType(val);
      if (row.key === "bundle_discount_value") setBundleValue(parseFloat(val) || 0);
      if (row.key === "bundle_min_services") setBundleMin(parseInt(val) || 2);
    });
  };

  useEffect(() => { fetchAll(); }, []);

  const saveBundleConfig = async () => {
    await Promise.all([
      supabase.from("app_settings").upsert({ key: "bundle_discount_type", value: JSON.stringify(bundleType) }, { onConflict: "key" }),
      supabase.from("app_settings").upsert({ key: "bundle_discount_value", value: JSON.stringify(bundleValue) }, { onConflict: "key" }),
      supabase.from("app_settings").upsert({ key: "bundle_min_services", value: JSON.stringify(bundleMin) }, { onConflict: "key" }),
    ]);
    toast({ title: "Configuração de desconto guardada!" });
  };

  const savePackage = async () => {
    if (!editing?.name) return;
    const { service_ids, ...rest } = editing as ServicePackage;

    if (editing.id) {
      const { id, ...updateData } = rest;
      await supabase.from("service_packages").update(updateData).eq("id", id);
      // Sync items
      await supabase.from("service_package_items").delete().eq("package_id", editing.id);
      if (service_ids?.length) {
        await supabase.from("service_package_items").insert(
          service_ids.map((sid) => ({ package_id: editing.id!, service_id: sid }))
        );
      }
    } else {
      const { data: newPkg } = await supabase.from("service_packages").insert({
        name: rest.name,
        description: rest.description || "",
        discount_type: rest.discount_type || "percentage",
        discount_value: rest.discount_value || 0,
        is_active: rest.is_active ?? true,
      }).select("id").single();

      if (newPkg && service_ids?.length) {
        await supabase.from("service_package_items").insert(
          service_ids.map((sid) => ({ package_id: newPkg.id, service_id: sid }))
        );
      }
    }

    toast({ title: "Pacote guardado!" });
    setOpen(false);
    setEditing(null);
    fetchAll();
  };

  const removePackage = async (id: string) => {
    await supabase.from("service_packages").delete().eq("id", id);
    toast({ title: "Pacote removido!" });
    fetchAll();
  };

  const toggleServiceInPackage = (serviceId: string) => {
    if (!editing) return;
    const ids = editing.service_ids || [];
    const updated = ids.includes(serviceId)
      ? ids.filter((id) => id !== serviceId)
      : [...ids, serviceId];
    setEditing({ ...editing, service_ids: updated });
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl font-bold">Pacotes & Descontos</h1>
      </div>

      <Tabs defaultValue="packages">
        <TabsList className="mb-6">
          <TabsTrigger value="packages" className="gap-1.5"><Package size={14} /> Pacotes</TabsTrigger>
          <TabsTrigger value="config" className="gap-1.5"><Settings2 size={14} /> Desconto Automático</TabsTrigger>
        </TabsList>

        <TabsContent value="packages">
          <div className="flex justify-end mb-4">
            <Button className="rounded-full gap-2" onClick={() => {
              setEditing({ name: "", description: "", discount_type: "percentage", discount_value: 0, is_active: true, service_ids: [] });
              setOpen(true);
            }}>
              <Plus size={16} /> Novo Pacote
            </Button>
          </div>

          <div className="space-y-3">
            {packages.map((p) => {
              const pkgServices = services.filter((s) => p.service_ids.includes(s.id));
              const subtotal = pkgServices.reduce((sum, s) => sum + s.price, 0);
              const disc = p.discount_type === "percentage" ? subtotal * (p.discount_value / 100) : p.discount_value;

              return (
                <div key={p.id} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-foreground">{p.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {pkgServices.length} serviços · €{(subtotal - Math.min(disc, subtotal)).toFixed(2)}
                        <span className="ml-2 text-primary text-xs">(-€{Math.min(disc, subtotal).toFixed(2)})</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.is_active ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                        {p.is_active ? "Ativo" : "Inativo"}
                      </span>
                      <Button size="icon" variant="ghost" onClick={() => { setEditing(p); setOpen(true); }}>
                        <Pencil size={16} />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => removePackage(p.id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {pkgServices.map((s) => (
                      <span key={s.id} className="bg-accent/50 text-xs px-2 py-0.5 rounded-full">{s.name}</span>
                    ))}
                  </div>
                </div>
              );
            })}
            {packages.length === 0 && (
              <p className="text-center text-muted-foreground py-8">Nenhum pacote criado. Crie o primeiro!</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="config">
          <div className="rounded-xl border border-border bg-card p-6 space-y-5 max-w-md">
            <p className="text-sm text-muted-foreground">
              Desconto automático quando o cliente seleciona múltiplos serviços individualmente (sem pacote).
            </p>
            <div>
              <Label>Tipo de desconto</Label>
              <div className="flex gap-2 mt-1">
                <Button variant={bundleType === "percentage" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setBundleType("percentage")}>
                  Percentagem (%)
                </Button>
                <Button variant={bundleType === "fixed" ? "default" : "outline"} size="sm" className="rounded-full" onClick={() => setBundleType("fixed")}>
                  Valor fixo (€)
                </Button>
              </div>
            </div>
            <div>
              <Label>Valor do desconto</Label>
              <Input type="number" value={bundleValue} onChange={(e) => setBundleValue(parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <Label>Mínimo de serviços</Label>
              <Input type="number" value={bundleMin} onChange={(e) => setBundleMin(parseInt(e.target.value) || 2)} />
              <p className="text-xs text-muted-foreground mt-1">Número mínimo de serviços para ativar o desconto.</p>
            </div>
            <Button className="rounded-full" onClick={saveBundleConfig}>Guardar configuração</Button>
          </div>
        </TabsContent>
      </Tabs>

      {/* Package edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editing?.id ? "Editar" : "Novo"} Pacote</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label>Nome</Label><Input value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label>Descrição</Label><Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo desconto</Label>
                  <div className="flex gap-1 mt-1">
                    <Button variant={editing.discount_type === "percentage" ? "default" : "outline"} size="sm" className="rounded-full text-xs flex-1" onClick={() => setEditing({ ...editing, discount_type: "percentage" })}>%</Button>
                    <Button variant={editing.discount_type === "fixed" ? "default" : "outline"} size="sm" className="rounded-full text-xs flex-1" onClick={() => setEditing({ ...editing, discount_type: "fixed" })}>€</Button>
                  </div>
                </div>
                <div><Label>Valor</Label><Input type="number" value={editing.discount_value || 0} onChange={(e) => setEditing({ ...editing, discount_value: parseFloat(e.target.value) })} /></div>
              </div>
              <div>
                <Label className="mb-2 block">Serviços incluídos</Label>
                <div className="space-y-2 max-h-[30vh] overflow-y-auto border border-border rounded-lg p-3">
                  {services.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={editing.service_ids?.includes(s.id) || false}
                        onCheckedChange={() => toggleServiceInPackage(s.id)}
                      />
                      <span className="text-sm">{s.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">€{s.price.toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label>Ativo</Label>
                <Switch checked={editing.is_active ?? true} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
              </div>
              <Button className="w-full rounded-full" onClick={savePackage}>Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackagesAdmin;
